<template>
  <div
    :style="style"
    :class="[
      'sg-step',
      isSimple ? 'is-simple' : `is-${parent.props.direction}`,
      isLast && !space && !isCenter && 'is-flex',
      isCenter && !isVertical && !isSimple && 'is-center',
    ]"
    @click="handleItemClick(index)"
  >
    <!-- icon & line -->
    <div :class="['sg-step__head', `is-${currentStatus}`]">
      <div :class="['sg-step__icon-wrap', `is-${icon ? 'icon' : 'text'}`]">
        <div :class="['sg-step__icon', `is-${icon ? 'icon' : 'text'}`]">
          <slot
            v-if="parent.props.activeStep <= index ||
              parent.props.completedSymbol !== 'check' &&
              parent.props.completedSymbol !== 'error'"
            name="icon"
          >
            <sg-icon v-if="icon" class="sg-step__icon-inner">
              <component :is="icon" />
            </sg-icon>
            <div v-if="!icon && !isSimple" class="sg-step__icon-inner">
              {{ index + 1 }}
            </div>
          </slot>
          <sg-icon v-else class="sg-step__icon-inner is-status">
            <Finished v-if="parent.props.completedSymbol === 'check'" />
            <Close v-else />
          </sg-icon>
        </div>
      </div>
      <div v-if="!isSimple && !isVertical && isContentRow" :class="['sg-step__title', `is-${currentStatus}`]"> <slot name="title">{{ title }}</slot></div>
      <div v-if="!isSimple" class="sg-step__line" :style="lineSize">
        <i class="sg-step__line-inner" :style="lineStyle"></i>
      </div>
    </div>
    <div class="sg-step__main">
        <div :class="['sg-step__title', `is-${currentStatus}`]" v-if="!isContentRow || isSimple || isVertical">
            <slot name="title">{{ title }}</slot>
        </div>
        <div v-if="isSimple" class="sg-step__arrow"></div>
        <div v-else :class="['sg-step__description', `is-${currentStatus}`]" :style="directionStyle">
            <slot name="description">{{ description }}</slot>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  reactive,
  watch
} from 'vue'
import { SgIcon } from '@sgui-plus/components/icon'
import { Close, Finished } from '@sgui-plus/icons-vue'

import type { Ref, PropType, Component } from 'vue'

export interface IStepsProps {
  space: number | string
  activeStep: number
  direction: string
  alignCenter: boolean
  simple: boolean
  finishStatus: string
  processStatus: string
  completedSymbol: string
  lineSize: number
  contentDirection: string
}

export interface StepItemState {
  uid: number
  currentStatus: string
  setIndex: (val: number) => void
  calcProgress: (status: string) => void
}

export interface IStepsInject {
  props: IStepsProps
  steps: Ref<StepItemState[]>
  stepClick: (index: number,props: IStepItemProps) => void
}

export interface IStepItemProps {
  title: string
  icon: string | Component
  description: string
  status: string
}

export default defineComponent({
  name: 'SgStep',
  components: {
    SgIcon,
    Close,
    Finished
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    icon: {
      type: [String, Object] as PropType<string | Component>,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    status: {
      type: String,
      default: '',
      validator: (val: string): boolean =>
        ['', 'wait', 'process', 'finish', 'error', 'success'].includes(val),
    },
  },
  emits:[
    'on-step-item-click'
  ],
  setup(props) {
    const index = ref(-1)
    const lineStyleStatus = ref('')
    const internalStatus = ref('')
    const parent: IStepsInject = inject('StepsContextKey')!
    const currentInstance = getCurrentInstance()

    onMounted(() => {
      watch(
        [
          () => parent.props.activeStep,
          () => parent.props.processStatus,
          () => parent.props.finishStatus,
        ],
        ([activeStep]) => {
          updateStatus(activeStep)
        },
        { immediate: true }
      )
    })

    onBeforeUnmount(() => {
      parent.steps.value = parent.steps.value.filter(
        (instance) => instance.uid !== currentInstance!.uid
      )
    })

    const currentStatus = computed(() => {
      return props.status || internalStatus.value
    })
    const prevStatus = computed(() => {
      const prevStep = parent.steps.value[index.value - 1]
      return prevStep ? prevStep.currentStatus : 'wait'
    })
    const isCenter = computed(() => {
      return parent.props.alignCenter
    })
    const isContentRow = computed(() => {
      return parent.props.contentDirection === 'row'
    })
    const isVertical = computed(() => {
      return parent.props.direction === 'vertical'
    })
    const isSimple = computed(() => {
      return parent.props.simple
    })
    const stepsCount = computed(() => {
      return parent.steps.value.length
    })
    const isLast = computed(() => {
      return (
        parent.steps.value[stepsCount.value - 1]?.uid === currentInstance!.uid
      )
    })
    const space = computed(() => {
      return isSimple.value ? '' : parent.props.space
    })
    const style = computed(() => {
      const style: Record<string, unknown> = {
        flexBasis:
          typeof space.value === 'number'
            ? `${space.value}px`
            : space.value
            ? space.value
            : `${100 / (stepsCount.value - (isCenter.value ? 0 : 1))}%`,
      }
      if (isVertical.value) return style
      if (isLast.value) {
        style.maxWidth = `${100 / stepsCount.value}%`
      }
      return style
    })

    const directionStyle = computed(() => {
      const style: Record<string, unknown> = {
        paddingLeft: !isSimple.value && !isVertical.value && isContentRow.value && !isCenter.value ?  '32px' : ''
      }

      return style
    })

    const lineSize = computed(() => {
      if(!parent.props.lineSize || parent.props.lineSize > 12 || parent.props.lineSize < 0) return
      let direction = isVertical.value ? 'width' : 'height'
      let offset = isVertical.value ? 'left' : 'top'
      return [
        `${direction}:${parent.props.lineSize}px`,
        `${offset}:${12-(parent.props.lineSize / 2)}px`
      ]
    })

    const setIndex = (val) => {
      index.value = val
    }
    const calcProgress = (status) => {
      lineStyleStatus.value = status
    }

    const lineStyle = computed(() => {
      const status = lineStyleStatus.value

      let step = 100
      const style: Record<string, unknown> = {color: '#F00'}

      style.transitionDelay = `${150 * index.value}ms`
      if (status === parent.props.processStatus) {
        step = 0
      } else if (status === 'wait') {
        step = 0
        style.transitionDelay = `${-150 * index.value}ms`
      }
      style.borderWidth = step && !isSimple.value ? '1px' : 0
      style[
        parent.props.direction === 'vertical' ? 'height' : 'width'
      ] = `${step}%`

      return style
    })

    const updateStatus = (activeIndex) => {
      if (activeIndex > index.value) {
        internalStatus.value = parent.props.finishStatus
      } else if (activeIndex === index.value && prevStatus.value !== 'error') {
        internalStatus.value = parent.props.processStatus
      } else {
        internalStatus.value = 'wait'
      }
      const prevChild = parent.steps.value[stepsCount.value - 1]
      if (prevChild) prevChild.calcProgress(internalStatus.value)
    }

    const handleItemClick = (index:number) => {
      if (index <= parent.props.activeStep) {
        const { title , icon, description, status } = props
        parent.stepClick(index, {title , icon, description, status})
      }
    }

    const stepItemState = reactive({
      uid: computed(() => currentInstance!.uid),
      currentStatus,
      setIndex,
      calcProgress,
    })
    parent.steps.value = [...parent.steps.value, stepItemState]

    return {
      index,
      lineStyle,
      lineSize,
      currentStatus,
      isCenter,
      isContentRow,
      isVertical,
      isSimple,
      isLast,
      space,
      style,
      parent,
      directionStyle,
      setIndex,
      calcProgress,
      updateStatus,
      handleItemClick,
    }
  },
})
</script>
